import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Factory, Loader2, Pencil, Plus, Search, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { JobberFormDialog } from '@/components/jobbers/JobberFormDialog';
import { JobberImportDialog } from '@/components/jobbers/JobberImportDialog';
import { errMsg } from '@/services/staffAttendanceApi';
import { jobberMasterApi } from '@/services/jobberMasterApi';

const STATUS = [['all', 'All'], ['active', 'Active'], ['inactive', 'Inactive']];
const hay = (j) => [j.name, j.legal_name, j.gstin, j.place, j.pincode, j.state, j.contact_person, j.phone, ...(j.categories || [])].join(' ').toLowerCase();
const catSlug = (c) => c.toLowerCase().replace(/[^a-z0-9]+/g, '-');
const catMatch = (j, cat) => cat === 'all' || (cat === 'none' ? !(j.categories || []).length : (j.categories || []).includes(cat));

const CategoryFilter = ({ value, onChange, categories }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="w-[210px] bg-white" data-testid="jobber-category-filter"><SelectValue /></SelectTrigger>
    <SelectContent>
      <SelectItem value="all" data-testid="jobber-category-filter-all">All categories</SelectItem>
      {categories.map((c) => <SelectItem key={c} value={c} data-testid={`jobber-category-filter-${catSlug(c)}`}>{c}</SelectItem>)}
      <SelectItem value="none" data-testid="jobber-category-filter-none">No category</SelectItem>
    </SelectContent>
  </Select>
);

const Stat = ({ label, value, tone = 'text-gray-900', testId }) => (
  <Card className="border-0 shadow-sm"><CardContent className="p-4">
    <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
    <div className={`text-2xl font-bold ${tone}`} data-testid={testId}>{value}</div>
  </CardContent></Card>
);

const Row = ({ j, canEdit, onEdit, onToggle }) => (
  <TableRow className={j.active ? '' : 'opacity-60'} data-testid={`jobber-row-${j.id}`}>
    <TableCell><div className="font-medium text-gray-900" data-testid={`jobber-name-${j.id}`}>{j.name}</div>
      {j.legal_name && j.legal_name !== j.name && <div className="text-xs text-gray-500">{j.legal_name}</div>}</TableCell>
    <TableCell data-testid={`jobber-gstin-${j.id}`}>{j.gstin === 'URP' ? <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-800">Unregistered</Badge> : <span className="font-mono text-xs">{j.gstin}</span>}</TableCell>
    <TableCell className="max-w-[260px]"><div className="truncate text-sm">{j.address1}</div>{j.address2 && <div className="truncate text-xs text-gray-500">{j.address2}</div>}</TableCell>
    <TableCell className="whitespace-nowrap text-sm" data-testid={`jobber-place-${j.id}`}>{j.place} · <span className="font-mono">{j.pincode}</span></TableCell>
    <TableCell className="text-sm">{j.state}</TableCell>
    <TableCell className="text-sm"><div>{j.contact_person || '—'}</div>{j.phone && <div className="font-mono text-xs text-gray-500">{j.phone}</div>}</TableCell>
    <TableCell className="max-w-[200px]" data-testid={`jobber-categories-${j.id}`}>
      {(j.categories || []).length ? <div className="flex flex-wrap gap-1">{j.categories.map((c) => <Badge key={c} variant="outline" className="border-sky-200 bg-sky-50 text-sky-800 text-[11px] whitespace-nowrap">{c}</Badge>)}</div>
        : <span className="text-xs text-gray-400">—</span>}
    </TableCell>
    <TableCell><Switch checked={!!j.active} disabled={!canEdit} onCheckedChange={(v) => onToggle(j, v)} data-testid={`jobber-active-${j.id}`} /></TableCell>
    {canEdit && <TableCell><Button size="sm" variant="ghost" onClick={() => onEdit(j)} data-testid={`jobber-edit-${j.id}`}><Pencil className="w-4 h-4" /></Button></TableCell>}
  </TableRow>
);

export default function JobberMaster() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('all');
  const [cat, setCat] = useState('all');
  const [editing, setEditing] = useState(undefined);
  const [importing, setImporting] = useState(false);
  const load = useCallback(() => jobberMasterApi.list().then(setD).catch((e) => toast.error(errMsg(e, 'Failed to load jobbers'))), []);
  useEffect(() => { load(); }, [load]);
  const all = d?.jobbers || [];
  const rows = useMemo(() => all.filter((j) => (status === 'all' || (status === 'active') === !!j.active) && catMatch(j, cat) && hay(j).includes(q.trim().toLowerCase())), [all, q, status, cat]);
  const toggle = async (j, v) => {
    try { await jobberMasterApi.setActive(j.id, v); toast.success(`${j.name} ${v ? 'activated' : 'set inactive'}`); load(); } catch (e) { toast.error(errMsg(e, 'Update failed')); }
  };
  const canEdit = !!d?.can_edit;
  const hb = 'bg-transparent text-white border-white/30 hover:bg-white/10';

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="jobber-master-page">
      <header className="bg-[#1A1F2E] text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate(-1)} className="text-white hover:bg-white/10" data-testid="jobber-master-back"><ArrowLeft className="w-5 h-5" /></Button>
          <Factory className="w-6 h-6 text-amber-400" />
          <h1 className="text-xl font-bold" data-testid="jobber-master-title">Jobber Master</h1>
          <div className="ml-auto flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => jobberMasterApi.exportXlsx().catch((e) => toast.error(errMsg(e, 'Export failed')))} disabled={!d} className={hb} data-testid="jobber-export-btn"><Download className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Export Excel</span></Button>
            {canEdit && <Button variant="outline" onClick={() => setImporting(true)} className={hb} data-testid="jobber-import-btn"><Upload className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Upload Excel</span></Button>}
            {canEdit && <Button onClick={() => setEditing(null)} className="bg-amber-500 hover:bg-amber-600 text-slate-900" data-testid="jobber-add-btn"><Plus className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Add jobber</span></Button>}
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 space-y-5">
        <p className="text-sm text-gray-600 max-w-3xl">Job orders (Inhouse, Flexi, Tissue, Shirt Mfg) can only pick <b>active</b> jobbers from here, and job-work e-way bills take the GSTIN, address, place, PIN and state from here.</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Jobbers" value={all.length} testId="jobber-stat-total" />
          <Stat label="Active" value={all.filter((j) => j.active).length} tone="text-emerald-700" testId="jobber-stat-active" />
          <Stat label="Inactive" value={all.filter((j) => !j.active).length} tone="text-rose-700" testId="jobber-stat-inactive" />
          <Stat label="Unregistered" value={all.filter((j) => j.gstin === 'URP').length} tone="text-amber-700" testId="jobber-stat-urp" />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[240px]"><Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, GSTIN, place, PIN, phone" className="pl-9 bg-white" data-testid="jobber-search" /></div>
          <div className="flex rounded-md border bg-white p-0.5" data-testid="jobber-status-filter">
            {STATUS.map(([v, l]) => <button key={v} type="button" onClick={() => setStatus(v)} className={`px-3 py-1.5 text-sm rounded ${status === v ? 'bg-[#1A1F2E] text-white' : 'text-gray-600 hover:bg-gray-100'}`} data-testid={`jobber-status-${v}`}>{l}</button>)}
          </div>
          <CategoryFilter value={cat} onChange={setCat} categories={d?.categories || []} />
        </div>
        <Card className="border-0 shadow-md"><CardContent className="p-0 overflow-x-auto">
          {!d ? <Loader2 className="w-6 h-6 animate-spin mx-auto my-10 text-amber-600" /> : (
            <Table data-testid="jobber-table">
              <TableHeader><TableRow>
                {['Jobber', 'GSTIN', 'Address', 'Place · PIN', 'State', 'Contact', 'Categories', 'Active'].map((h) => <TableHead key={h}>{h}</TableHead>)}
                {canEdit && <TableHead />}
              </TableRow></TableHeader>
              <TableBody>
                {rows.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-12 text-gray-500" data-testid="jobber-empty">{all.length ? 'No jobber matches' : 'No jobbers yet — add one or upload the Excel template'}</TableCell></TableRow>}
                {rows.map((j) => <Row key={j.id} j={j} canEdit={canEdit} onEdit={setEditing} onToggle={toggle} />)}
              </TableBody>
            </Table>
          )}
        </CardContent></Card>
      </main>
      <JobberFormDialog open={editing !== undefined} jobber={editing} states={d?.states} categories={d?.categories} onClose={() => setEditing(undefined)} onSaved={() => { setEditing(undefined); load(); }} />
      <JobberImportDialog open={importing} onClose={() => setImporting(false)} onDone={load} />
    </div>
  );
}
